import { useState } from 'react';
import { ApiError } from '@/lib/api';

export type SubmitStatus = 'idle' | 'loading' | 'success' | 'error';

export function mapError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 429) return 'Demasiados intentos. Probá de nuevo en unos minutos.';
    if (err.code === 'validation_error') return 'Revisá los datos ingresados.';
    return 'Ocurrió un problema al enviar. Probá de nuevo.';
  }
  return 'No pudimos enviar el formulario. Verificá tu conexión e intentá otra vez.';
}

export function useFormSubmit<TInput>(submitFn: (input: TInput) => Promise<unknown>) {
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function submit(input: TInput, honeypot: string): Promise<boolean> {
    // Honeypot client-side: o SDK faz zod-strip de chaves desconhecidas, então
    // `website` nunca chegaria ao backend via SDK. Bot que preencheu o campo
    // oculto recebe sucesso simulado, sem disparar request de rede. Hits diretos
    // à API (curl) são cobertos pelo honeypot server-side.
    if (honeypot.trim() !== '') {
      setStatus('success');
      return true;
    }
    setStatus('loading');
    setErrorMessage(null);
    try {
      await submitFn(input);
      setStatus('success');
      return true;
    } catch (err) {
      setStatus('error');
      setErrorMessage(mapError(err));
      return false;
    }
  }

  function reset() {
    setStatus('idle');
    setErrorMessage(null);
  }

  return { status, errorMessage, submit, reset };
}
